'use client';

import React from 'react';

export interface ChipProps {
  selected?: boolean;
  /** Optional leading node (icon / dot). */
  leading?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

/**
 * Filter / category chip. Selected = accent/100 fill + accent/600 text.
 */
export function Chip({
  selected = false,
  leading = null,
  onClick,
  disabled = false,
  children,
  style = {},
  ...rest
}: ChipProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ChipProps>) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        height: 36,
        paddingInline: 'var(--sp-4)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-body)',
        fontWeight: (selected
          ? 'var(--fw-semibold)'
          : 'var(--fw-medium)') as React.CSSProperties['fontWeight'],
        color: selected ? 'var(--accent-deep)' : 'var(--text-secondary)',
        background: selected ? 'var(--brand-wash)' : 'var(--bg-surface)',
        border: `1px solid ${selected ? 'transparent' : 'var(--border-subtle)'}`,
        borderRadius: 'var(--r-sm)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'background var(--dur-micro), color var(--dur-micro)',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {leading}
      {children}
    </button>
  );
}
